import { computed, inject, Injectable, signal } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { defer, filter, map, Observable, of, take } from 'rxjs';
import { BackendService } from '@core/services';
import { ScanCheckMode } from '@shared/constants';
import { Product } from '@shared/factory';
import { barcodesMatch } from '@shared/utils';

/** Settled (or in-flight) state of {@link ProductCheckService.productsResource}. */
interface LoadedList {
  epoch: number;
  error: unknown;
  products: Product[] | null;
}

@Injectable()
export class ProductCheckService {
  private readonly backendService = inject(BackendService);
  private readonly checkMode = signal(ScanCheckMode.PRODUCT_LIST);
  private readonly offerId = signal<number | undefined>(undefined);
  private readonly campaignId = signal<number | undefined>(undefined);
  private readonly productScopeId = signal<number | undefined>(undefined);

  /**
   * Eligible products for the active challenge. Loads lazily once a scope is
   * set via {@link setScope}; exposes `value()`, `isLoading()`, `error()`.
   */
  readonly productsResource = rxResource({
    defaultValue: [] as Product[],
    params: () => this.productScopeId(),
    stream: ({ params }) => this.backendService.getProductsList(params),
  });

  /**
   * Bumped by {@link retryProductsIfFailed}. `loadedList$` carries it so a check
   * can tell a settled state produced *after* its retry from the stale one
   * `toObservable` replays synchronously on subscribe.
   */
  private readonly reloadEpoch = signal(0);

  /**
   * The product list once loading has settled — `products` is `null` while the
   * fetch is still in flight. Lets an eligibility check that fires before the
   * list resolves wait for it instead of matching against the empty default.
   */
  private readonly loadedList$: Observable<LoadedList> = toObservable(
    computed<LoadedList>(() => ({
      epoch: this.reloadEpoch(),
      error: this.productsResource.error(),
      products: this.productsResource.isLoading()
        ? null
        : this.productsResource.hasValue()
          ? this.productsResource.value()
          : [],
    }))
  );

  setScope(productScopeId: number): void {
    this.productScopeId.set(productScopeId);
  }

  /**
   * Offer the barcode is checked against in {@link ScanCheckMode.SERVER} mode.
   */
  setOfferId(offerId: number): void {
    this.offerId.set(offerId);
  }

  /**
   * Campaign the offer belongs to; part of the server-side check endpoint path.
   */
  setCampaignId(campaignId: number): void {
    this.campaignId.set(campaignId);
  }

  setCheckMode(checkMode: ScanCheckMode): void {
    this.checkMode.set(checkMode);
  }

  /**
   * Retries a failed product-list fetch: the resource keeps its error for the
   * modal's lifetime otherwise, so every later check would re-throw it.
   *
   * The epoch is bumped only when `reload()` reports it actually started one.
   * `error()` alone cannot tell: a resource reloading after a failure keeps
   * returning its old error (the errored stream is retained while the request
   * is unchanged) even though `reload()` no-ops on an already-loading resource.
   * Bumping regardless would strand every check still waiting on the previous
   * epoch — they would never see a matching emission and never settle.
   */
  retryProductsIfFailed(): void {
    if (this.productsResource.error() === undefined) {
      return;
    }
    if (!this.productsResource.reload()) {
      return;
    }
    this.reloadEpoch.update((epoch: number) => epoch + 1);
  }

  /**
   * Barcode eligibility check. Routes to the strategy chosen via
   * {@link setCheckMode} (configured per retailer by the scan tab `checkMode`),
   * unless the caller passes its own mode (e.g. the manual entry tab, whose
   * mode must not depend on a scan tab being present).
   */
  isEligible(
    barcode: string,
    checkMode: ScanCheckMode = this.checkMode()
  ): Observable<Product | null> {
    return checkMode === ScanCheckMode.SERVER
      ? this.checkAgainstServer(barcode)
      : this.checkAgainstProductList(barcode);
  }

  /**
   * `barcodesMatch` handles the numeric GTIN family our retailers scan at POS
   * across all regions: UPC-A/UPC-E (US) and EAN-13/EAN-8 (UK, EU, AU). It
   * matches across length, leading-zero and check-digit differences (e.g. a
   * 12-digit scan vs a 10-digit stored core).
   */
  private checkAgainstProductList(barcode: string): Observable<Product | null> {
    // `defer` so the epoch is read per subscription, not when `isEligible` is
    // called: a caller that stores the observable, retries it or subscribes
    // twice would otherwise carry an epoch from an earlier retry and wait for
    // an emission that has already gone by.
    return defer(() => {
      // The epoch a usable answer must carry. `toObservable` replays its last
      // value synchronously on subscribe, so a caller that just called
      // `retryProductsIfFailed` would otherwise be handed the pre-retry state
      // (the stale error, or the empty default that masquerades as "not
      // eligible") before the retry has had a chance to run.
      const epoch: number = this.reloadEpoch();
      return this.loadedList$.pipe(
        // Wait until the list has finished loading (skip the `null` in-flight
        // window) so a fast scan doesn't match against the empty default and
        // falsely report "not eligible".
        // `>=`, not `===`: the epoch is monotone, so a strictly smaller one is
        // always the stale pre-retry replay, while a larger one is a settled
        // state at least as fresh as the caller asked for. Requiring equality
        // would strand a check whose epoch is overtaken before it ever sees a
        // settled emission.
        filter(
          (list: LoadedList) => list.epoch >= epoch && list.products !== null
        ),
        take(1),
        map(({ error, products }: LoadedList) => {
          // A failed list fetch must surface as a failure, not as an empty
          // list silently matching nothing ("not eligible" would be a false
          // verdict).
          if (error !== undefined) {
            throw error;
          }
          // `products` is non-null past the filter; `?? []` keeps that
          // implicit narrowing honest without a cast.
          return (
            (products ?? []).find((product: Product) =>
              barcodesMatch(barcode, product.barcodeNumber)
            ) ?? null
          );
        })
      );
    });
  }

  private checkAgainstServer(barcode: string): Observable<Product | null> {
    const campaignId: number | undefined = this.campaignId();
    const offerId: number | undefined = this.offerId();
    if (campaignId === undefined || offerId === undefined) {
      console.error(
        'Cannot run server-side product check: campaign or offer id is not set.'
      );
      return of(null);
    }
    return this.backendService.getEligibleProduct(campaignId, offerId, barcode);
  }
}
